'use client'

import React, { useEffect, useRef, useState } from 'react'

interface NewsItem {
    id: string | number
    headline?: string
    companyName?: string
    image?: string
}

interface NewsBannerProps {
    items: NewsItem[]
    backgroundColor?: string
    textColor?: string
    speed?: 'slow' | 'medium' | 'fast'
    height?: number
    gradientColors?: string[]
    showCompanyName?: boolean
}

export default function NewsBanner({
    items,
    backgroundColor = 'rgb(200, 143, 95)',
    textColor = '#fff',
    speed = 'slow',
    height = 80,
    gradientColors = ['rgba(200, 143, 95, 0.9)', 'rgba(150, 100, 70, 0.95)'],
    showCompanyName = true,
}: NewsBannerProps) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [scrollWidth, setScrollWidth] = useState(0)
    const [containerWidth, setContainerWidth] = useState(0)
    const [duplicatedItems, setDuplicatedItems] = useState<NewsItem[]>([])

    const getAnimationDuration = () => {
        const baseSpeed = {
            slow: 3,
            medium: 2,
            fast: 1,
        }

        const contentLength = scrollWidth / 2 || 1000
        const speedFactor = baseSpeed[speed]

        return Math.max((contentLength / 50) * speedFactor, 15)
    }

    useEffect(() => {
        if (items.length === 0) return

        let duplicated = [...items]
        while (duplicated.length < 10) {
            duplicated = [...duplicated, ...items]
        }

        setDuplicatedItems(duplicated)
    }, [items])

    useEffect(() => {
        const updateWidths = () => {
            if (scrollRef.current && containerRef.current) {
                setScrollWidth(scrollRef.current.scrollWidth)
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }

        updateWidths()

        window.addEventListener('resize', updateWidths)

        const timeoutId = setTimeout(updateWidths, 100)

        return () => {
            window.removeEventListener('resize', updateWidths)
            clearTimeout(timeoutId)
        }
    }, [])

    const styles = {
        container: {
            color: textColor,
            overflow: 'hidden',
            width: '100%',
            position: 'relative' as const,
            height: `${height}px`,
            display: 'flex',
            alignItems: 'center',
            zIndex: 1,
        },
        scrollContainer: {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            whiteSpace: 'nowrap' as const,
            willChange: 'transform',
            animation: `scroll ${getAnimationDuration()}s linear infinite`,
        },
        newsItem: {
            display: 'flex',
            alignItems: 'center',
            padding: '0 20px',
            height: '100%',
        },
        companyLogo: {
            height: `${height * 1}px`,
            width: `${height * 1}px`,
            marginRight: '15px',
            objectFit: 'contain' as const,
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            padding: '4px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.2s ease-in-out',
        },
        companyName: {
            fontWeight: 'bold',
            marginRight: '15px',
            fontSize: `${Math.min(height * 0.25, 18)}px`,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        },
        headline: {
            whiteSpace: 'nowrap' as const,
            fontSize: `${Math.min(height * 0.2, 16)}px`,
            opacity: 0.9,
            fontWeight: 500,
        },
        dot: {
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: textColor,
            margin: '0 20px',
            verticalAlign: 'middle',
            opacity: 0.7,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        },
    }

    const mobileStyles = {
        companyLogo: {
            height: `${height * 0.5}px`,
            width: `${height * 0.5}px`,
            marginRight: '10px',
        },
        companyName: {
            fontSize: `${Math.min(height * 0.15, 12)}px`,
            marginRight: '10px',
        },
        headline: {
            fontSize: `${Math.min(height * 0.12, 10)}px`,
        },
        dot: {
            width: '4px',
            height: '4px',
            margin: '0 10px',
        },
    }

    const keyframes = `
  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-${scrollWidth / 2}px);
    }
  }
`

    return (
        <div ref={containerRef} style={styles.container}>
            <style>{keyframes}</style>

            <div ref={scrollRef} style={styles.scrollContainer}>
                {duplicatedItems.map((item, index) => (
                    <React.Fragment key={`${item.id}-${index}`}>
                        <div style={styles.newsItem}>
                            {item.image && (
                                <img
                                    src={item.image}
                                    alt={item.companyName || item.headline || ''}
                                    style={{
                                        ...styles.companyLogo,
                                        ...(window.innerWidth <= 480 ? mobileStyles.companyLogo : {}),
                                    }}
                                />
                            )}
                            {showCompanyName && item.companyName && (
                                <span
                                    style={{
                                        ...styles.companyName,
                                        ...(window.innerWidth <= 480 ? mobileStyles.companyName : {}),
                                    }}
                                >
                                    {item.companyName}
                                </span>
                            )}
                            {item.headline && (
                                <span
                                    style={{
                                        ...styles.headline,
                                        ...(window.innerWidth <= 480 ? mobileStyles.headline : {}),
                                    }}
                                >
                                    {item.headline}
                                </span>
                            )}
                        </div>
                        <span
                            style={{
                                ...styles.dot,
                                ...(window.innerWidth <= 480 ? mobileStyles.dot : {}),
                            }}
                        ></span>
                    </React.Fragment>
                ))}

                {/* Duplicate the items to create a loop */}
                {duplicatedItems.map((item, index) => (
                    <React.Fragment key={`${item.id}-${index}-dup`}>
                        <div style={styles.newsItem}>
                            {item.image && (
                                <img
                                    src={item.image}
                                    alt={item.companyName || item.headline || ''}
                                    style={{
                                        ...styles.companyLogo,
                                        ...(window.innerWidth <= 480 ? mobileStyles.companyLogo : {}),
                                    }}
                                />
                            )}
                            {showCompanyName && item.companyName && (
                                <span
                                    style={{
                                        ...styles.companyName,
                                        ...(window.innerWidth <= 480 ? mobileStyles.companyName : {}),
                                    }}
                                >
                                    {item.companyName}
                                </span>
                            )}
                            {item.headline && (
                                <span
                                    style={{
                                        ...styles.headline,
                                        ...(window.innerWidth <= 480 ? mobileStyles.headline : {}),
                                    }}
                                >
                                    {item.headline}
                                </span>
                            )}
                        </div>
                        <span
                            style={{
                                ...styles.dot,
                                ...(window.innerWidth <= 480 ? mobileStyles.dot : {}),
                            }}
                        ></span>
                    </React.Fragment>
                ))}
            </div>
        </div>
    )
}
